import { useState } from "react";
import { Card, CardContent } from "@/common/components/ui/card";
import { Input } from "@/common/components/ui/input";
import { Button } from "@/common/components/ui/button";
import { Search, TrendingUp, RefreshCw, Layers, Eye } from "lucide-react";
import AntDTable from "@/common/components/ui/antd-table";
import { useNavigate } from "react-router-dom";
import finEaseHomeNavigationRoutes from "../../../../utils/navigationRoutes";

const IPOAllotmentList = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(
    {}
  );

  const handleColumnFilterChange = (columnKey: string, value: string) => {
    setColumnFilters(prev => ({ ...prev, [columnKey]: value }));
  };

  // Mock data based on the screenshot
  const mockData = [
    {
      id: "1",
      ipo_date: "16-01-2026",
      ipo_name: "Bharat Coking Coal Limited",
      count: 6,
    },
    {
      id: "2",
      ipo_date: "18-12-2025",
      ipo_name: "ICICI Prudential Asset Management Company Limited",
      count: 33,
    },
    {
      id: "3",
      ipo_date: "16-12-2025",
      ipo_name: "Park Medi World Limited",
      count: 4,
    },
    {
      id: "4",
      ipo_date: "12-12-2025",
      ipo_name: "Wakefit Innovations Limited",
      count: 2,
    },
    {
      id: "5",
      ipo_date: "12-12-2025",
      ipo_name: "CORONA REMEDIES LIMITED",
      count: 2,
    },
    { id: "6", ipo_date: "09-12-2025", ipo_name: "Aequs Limited", count: 3 },
    { id: "7", ipo_date: "09-12-2025", ipo_name: "MEESHO LIMITED", count: 2 },
    {
      id: "8",
      ipo_date: "09-12-2025",
      ipo_name: "Vidya Wires Limited",
      count: 1,
    },
  ];

  const filteredData = mockData.filter(item => {
    const matchesSearch = item.ipo_name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesColumnFilters = Object.entries(columnFilters).every(
      ([key, value]) => {
        if (!value) {
          return true;
        }
        return String(item[key as keyof typeof item])
          .toLowerCase()
          .includes(value.toLowerCase());
      }
    );
    return matchesSearch && matchesColumnFilters;
  });

  const handleSync = () => {
    setLoading(true);
    setTimeout(() => setLoading(false), 1000);
  };

  const columns = [
    {
      title: "IPO DATE",
      dataIndex: "ipo_date",
      key: "ipo_date",
      width: 150,
      className: "text-sm font-medium text-slate-600",
      sorter: (a: any, b: any) => a.ipo_date.localeCompare(b.ipo_date),
    },
    {
      title: "IPO NAME",
      dataIndex: "ipo_name",
      key: "ipo_name",
      className: "text-sm font-bold text-slate-800",
      render: (text: string) => (
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-primary/70" />
          <span>{text}</span>
        </div>
      ),
      sorter: (a: any, b: any) => a.ipo_name.localeCompare(b.ipo_name),
    },
    {
      title: "COUNT",
      dataIndex: "count",
      key: "count",
      width: 100,
      align: "center" as const,
      className: "text-sm font-bold text-slate-700",
      sorter: (a: any, b: any) => a.count - b.count,
    },
    {
      title: "ACTION",
      key: "action",
      width: 150,
      align: "center" as const,
      render: (_: any, record: any) => (
        <Button
          variant="ghost"
          size="icon"
          onClick={() =>
            navigate(
              finEaseHomeNavigationRoutes.IPO_ALLOTMENT_DETAILS(record.id)
            )
          }
          className="h-8 w-8 text-[#0070c0] transition-all hover:bg-[#0070c0]/10 hover:text-[#005fa3]"
        >
          <Eye className="h-4 w-4" />
        </Button>
      ),
    },
  ];

  return (
    <div className="min-h-screen space-y-6 bg-gray-50/50 p-6">
      {/* Enhanced Header - Matching Screenshot 2 style */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-secondary via-secondary/95 to-primary/80 p-6 shadow-lg shadow-primary/10 md:p-8">
        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent" />
        <svg
          className="absolute right-0 top-0 h-full w-1/3 text-white/5 opacity-50"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <polygon fill="currentColor" points="50,0 100,0 100,100 0,100" />
        </svg>
        <div className="relative flex flex-col justify-between gap-4 md:flex-row md:items-center">
          <div className="flex items-center gap-5">
            <div className="rounded-xl border border-white/10 bg-white/20 p-3 shadow-inner backdrop-blur-md">
              <Layers className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="font-display text-3xl font-bold tracking-tight text-white">
                IPO Allotment
              </h1>
              <p className="font-medium text-white/80">
                Manage and review IPO allotment data and client distributions
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              className="h-11 gap-2 border-white/20 bg-white/10 text-white backdrop-blur-sm hover:bg-white/20"
              onClick={handleSync}
              disabled={loading}
            >
              <RefreshCw
                className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
              />
              Sync Data
            </Button>
          </div>
        </div>
      </div>

      {/* Results Info & Filter Bar */}
      <div className="flex items-center justify-between px-2">
        <p className="text-sm font-medium text-slate-500">
          Showing{" "}
          <span className="font-bold text-slate-900">
            {filteredData.length}
          </span>{" "}
          registered IPOs
        </p>
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search by IPO Name..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="h-10 rounded-lg border-slate-200 bg-white pl-10 shadow-sm focus:ring-primary/10"
          />
        </div>
      </div>

      {/* Main Content Table */}
      <Card className="overflow-hidden rounded-2xl border-slate-200 bg-white shadow-xl shadow-slate-200/50">
        <CardContent className="p-0">
          <AntDTable
            columns={columns}
            data={filteredData}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total: number) => `Total ${total} IPOs`,
            }}
            showSettings
            showColumnFilter
            onFilterChange={handleColumnFilterChange}
            rowKey="id"
            className="allotment-table-enhanced"
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default IPOAllotmentList;
